import React, { useState } from 'react'
import { makeStyles, tokens, Text } from '@fluentui/react-components'
import { ConsequenceAndProbabilityRisk, RiskAnalysis, RiskAnalysisStatus } from '../../../../api/schemas/schema'
import { t } from '../../../../i18n/i18n'
import { CardHeader } from '../../../common/header/CardHeader'
import { List } from '../../../common/list/List'
import { getConsequenceProbabilityRiskEvaluateColumns } from '../../../../helpers/riskAnalysisHelper'
import { ModalConsequenceEvaluate } from './modals/ModalConsequenceEvaluate'
import { RiskLevelWithNumberBadge } from '../../RiskBadges'
import { BadgeConsequenceProbability } from './BadgeConsequenceProbability'
import { ConsequenceScaleLabel } from './ConsequenceScaleLabel'
import { IItemState } from '../../../../interfaces/IItemState'
import { InfoMessageBar } from '../../../common/other/InfoMessageBar'
import { sortConsequenceRisks } from '../../../../helpers/riskManagementHelper'

const useStyles = makeStyles({
   buttonWrapper: {
      '@media screen and (max-width: 1007px)': {
         display: 'flex',
         justifyContent: 'space-between',
         gap: tokens.spacingHorizontalM,
         '& > button': {
            outline: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
            flexGrow: 1,
            width: '50%',
            padding: tokens.spacingVerticalXS,
         },
      },
   },
   phoneItemWrapper: {
      '& > *': {
         marginBottom: tokens.spacingVerticalMNudge,
      },
      display: 'flex',
      flexDirection: 'column',
      marginBottom: tokens.spacingVerticalM,
   },
   badgeWrapper: {
      display: 'flex',
      '& > *': {
         paddingRight: tokens.spacingHorizontalL,
      },
      '& > div': {
         display: 'flex',
         gap: tokens.spacingHorizontalS,
      },
   },
})

type ConsequenceEvaluationProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysisState: IItemState<RiskAnalysis>
}
export const ConsequenceEvaluation = ({ analysisState, setRiskAnalysisState }: ConsequenceEvaluationProps) => {
   const classes = useStyles()
   const [modals, setModals] = useState({
      determineModal: false,
      evaluateModal: false,
   })
   const [checkboxHasLocalState, setCheckboxHasLocalState] = useState<boolean>(false)
   const [selectedRisk, setSelectedRisk] = useState<any>(null)
   const [manualIsDirty, setManualIsDirty] = React.useState<boolean>(false)

   const analysis = analysisState.item
   const disabled = analysisState.disabled || analysis.status === RiskAnalysisStatus.Completed

   const toggleModal = (modalName: string, isOpen: boolean) => {
      setModals({
         determineModal: false,
         evaluateModal: false,
         [modalName]: isOpen,
      })
   }

   const handleRowClick = (risk: any) => {
      setSelectedRisk(risk)
      toggleModal('evaluateModal', true)
   }

   const onActionRequiredClick = (risk: ConsequenceAndProbabilityRisk, columnKey: keyof ConsequenceAndProbabilityRisk) => {
      if (columnKey === 'proposedAction' && !disabled) {
         risk = toggleCheckbox(risk, true)
         setManualIsDirty(true)
      }
      handleRowClick(risk)
   }

   const toggleCheckbox = (risk: ConsequenceAndProbabilityRisk, openModal?: boolean) => {
      risk = { ...risk, actionRequired: !risk.actionRequired }

      const updatedrisks = analysis.consequenceAndProbabilityRisks.map((item) => (item.id === risk.id ? risk : item))

      const updatedAnalysis = { ...analysis, consequenceAndProbabilityRisks: updatedrisks }
      setRiskAnalysisState((prev) => ({
         ...prev,
         item: updatedAnalysis,
      }))
      openModal && setCheckboxHasLocalState(true)
      return risk
   }

   const phoneBody = (risk: ConsequenceAndProbabilityRisk) => {
      return (
         <span>
            <div className={classes.phoneItemWrapper}>
               <RiskLevelWithNumberBadge largeBadge risk={risk} />
               <div className={classes.badgeWrapper}>
                  <div>
                     <ConsequenceScaleLabel typeLabel="L" />
                     <BadgeConsequenceProbability valuation={risk.consequenceLifeHealth} />
                  </div>

                  <div>
                     <ConsequenceScaleLabel typeLabel="E" />
                     <BadgeConsequenceProbability valuation={risk.consequenceEconomy} />
                  </div>

                  <div>
                     <ConsequenceScaleLabel typeLabel="M" />
                     <BadgeConsequenceProbability valuation={risk.consequenceEnviroment} />
                  </div>

                  <div>
                     <ConsequenceScaleLabel typeLabel="V" />
                     <BadgeConsequenceProbability valuation={risk.consequenceBrand} />
                  </div>

                  <div>
                     <Text weight="bold" size={400}>
                        S
                     </Text>
                     <BadgeConsequenceProbability valuation={risk.probability} />
                  </div>
               </div>
               <Text>{risk.scenario}</Text>
            </div>
         </span>
      )
   }

   return (
      <>
         <CardHeader title={t('RiskEvaluation')} />
         {analysis.consequenceAndProbabilityRisks.length === 0 ? (
            <InfoMessageBar title={t('NoRisksIdentifiedYet')} />
         ) : (
            <>
               <List
                  columns={getConsequenceProbabilityRiskEvaluateColumns(disabled)}
                  columnSizingOptions={{
                     riskScenario: { autoFitColumns: true, minWidth: 150, idealWidth: 400 },
                     consequence: { autoFitColumns: true, minWidth: 150, idealWidth: 400 },
                     consequenceScaleLifeHealth: { autoFitColumns: true, minWidth: 35, idealWidth: 70 },
                     consequenceScaleEconomy: { autoFitColumns: true, minWidth: 35, idealWidth: 70 },
                     consequenceScaleEnvironment: { autoFitColumns: true, minWidth: 35, idealWidth: 70 },
                     consequenceScaleBrand: { autoFitColumns: true, minWidth: 50, idealWidth: 80 },
                     probability: { autoFitColumns: true, minWidth: 50, idealWidth: 80 },
                     riskLevel: { autoFitColumns: true, minWidth: 160 },
                     proposedAction: { autoFitColumns: true, minWidth: 100 },
                  }}
                  resizableColumns
                  items={sortConsequenceRisks(analysis.consequenceAndProbabilityRisks)}
                  onRowClick={handleRowClick}
                  onCellClick={onActionRequiredClick}
                  renderPhoneBodyItem={phoneBody}
               />

               <ModalConsequenceEvaluate
                  setRiskAnalysisState={setRiskAnalysisState}
                  analysis={analysis}
                  open={modals.evaluateModal}
                  checkboxHasLocalState={checkboxHasLocalState}
                  close={() => {
                     toggleModal('analyseEvaluateModal', false)
                     setCheckboxHasLocalState(false)
                     setManualIsDirty(false)
                  }}
                  toggleCheckbox={toggleCheckbox}
                  selectedRisk={selectedRisk}
                  disabled={disabled || selectedRisk?.determined}
                  manualIsDirty={manualIsDirty}
               />
            </>
         )}
      </>
   )
}
